import React, { useEffect, useState } from 'react';

import { ErrorPrompt } from '@opendoor/bricks/complex';
import { Button, Flex, Text } from '@opendoor/bricks/core';

import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';
import { usePrevious } from 'shared/helpers/hooks/usePrevious';

import { ContactForm } from 'buying/components/buyDirectCheckout/ContactForm';
import {
  ContactDetail,
  validateContactDetails,
} from 'buying/stateMachines/buyDirectCheckoutFlowStateMachine';

const PRIMARY_BUYER_KEY = 0;

interface IBackupOfferProps {
  buyerDetails?: Record<string, ContactDetail>;
  send: (
    args:
      | string
      | { type: string; value: string | boolean | Record<string, ContactDetail> | number },
  ) => void;
}

/**
 * Contact Details
 * storybook-check-ignore
 */
export const BackupOffer: React.FC<IBackupOfferProps> = React.memo(({ buyerDetails, send }) => {
  const { isMobile } = useMobileDevice();
  const [contactDetails, setContactDetails] = useState<Record<string, ContactDetail>>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const prevBuyerDetails = usePrevious(buyerDetails);

  useEffect(() => {
    // buyerDetails is set asynchronously so we need to hydrate state once the initial fetch is complete
    if (!prevBuyerDetails && buyerDetails) {
      setContactDetails(buyerDetails);
    }
  }, [prevBuyerDetails, buyerDetails]);

  const handleOnFormFieldChange = (
    key: string,
    field: string,
    value: string | number | boolean,
  ) => {
    setContactDetails((prevState) => {
      return {
        ...prevState,
        [key]: {
          ...contactDetails?.[key],
          id: key,
          [field]: value,
        },
      };
    });
  };

  const handleOnShareDetails = () => {
    const formValidationError = validateContactDetails(contactDetails);
    if (formValidationError) {
      setErrorMessage(formValidationError);
      return window.scrollTo(0, 0);
    }
    send({ type: 'setContactDetails', value: contactDetails });
    handleForward();
  };

  const handleForward = () => {
    send('FORWARD');
    window.scrollTo(0, 0);
  };

  return (
    <Flex flexDirection="column" alignItems={isMobile ? 'center' : undefined} width="100%">
      <Text
        alignSelf="flex-start"
        size={isMobile ? '160' : '150'}
        fontWeight="medium"
        mb={7}
        ml={[6, null, 0]}
      >
        Contact details
      </Text>
      <ErrorPrompt message={errorMessage} />
      <ContactForm contactDetails={buyerDetails} index={0} onFormChange={handleOnFormFieldChange} />
      <Button
        alignSelf={['center', null, 'flex-end']}
        analyticsName="buy-direct-checkout-backup-offer-share-details-button"
        aria-label="Share details"
        data-testid="buy-direct-checkout-backup-offer-share-details-button"
        disabled={
          !contactDetails?.[PRIMARY_BUYER_KEY]?.email ||
          !contactDetails?.[PRIMARY_BUYER_KEY]?.phone ||
          !contactDetails?.[PRIMARY_BUYER_KEY]?.name ||
          !contactDetails?.[PRIMARY_BUYER_KEY]?.emailVerification
        }
        width={['90%', null, '25%']}
        variant="primary"
        onClick={handleOnShareDetails}
        mt={8}
      >
        Share details
      </Button>
    </Flex>
  );
});

BackupOffer.displayName = 'BackupOffer';
